import axios from 'axios';

/**
 * The message a failed request should show. NestJS puts validation failures in `message` as an
 * array and everything else as a string; anything that is not an axios error carries nothing worth
 * showing a user. The fallback is passed in rather than defaulted so it is always a translated
 * string chosen by the caller.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return fallback;
  const data = error.response?.data as
    | { message?: string | string[]; errors?: string | string[] | Record<string, unknown> }
    | undefined;
  return flatten(data?.message) ?? flatten(data?.errors) ?? fallback;
};

/**
 * `errors` is the 7k's shape and `message` the microservices'. Both were being read separately, and
 * the 7k's was read straight into `intl.formatMessage({ id })`, so a patient-create failure showed
 * "Well, this is unexpected / 400" with the server's actual complaint discarded (QA v26.10, #16).
 * The 7k returns it as a string, a list, or a map of field to problem.
 */
const flatten = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value || undefined;
  if (Array.isArray(value)) {
    const joined = value.filter((entry) => typeof entry === 'string').join(', ');
    return joined || undefined;
  }
  if (value && typeof value === 'object') {
    const joined = Object.values(value)
      .map((entry) => flatten(entry))
      .filter(Boolean)
      .join(', ');
    return joined || undefined;
  }
  return undefined;
};
